import type { BlockNoteEditor } from '@blocknote/core';

import { createBlock, fetchBlockTree } from '@/lib/blocks/api';
import { newBlockId } from '@/lib/blocks/ids';
import type { BlockRecord } from '@/lib/blocks/types';
import {
  DATABASE_EMBED_BLOCK_TYPE,
  DEFAULT_DATABASE_EMBED_VIEW_TYPE,
  type DatabaseEmbedViewType,
} from './database-embed-block.web';

const DEFAULT_TITLE = 'Untitled database';

export function slashBlockShouldBeReplaced(content: unknown): boolean {
  if (!Array.isArray(content)) {
    return false;
  }
  if (content.length === 0) {
    return true;
  }
  if (content.length === 1 && (content[0] as { type?: string })?.type === 'text') {
    const text = String((content[0] as { text?: string }).text ?? '');
    return text === '' || text.startsWith('/');
  }
  return false;
}

function insertDatabaseLinkParagraph(editor: BlockNoteEditor, href: string, title: string) {
  const block = editor.getTextCursorPosition().block;
  const linkParagraph = {
    type: 'paragraph' as const,
    content: [
      {
        type: 'link' as const,
        href,
        content: [{ type: 'text' as const, text: title, styles: {} }],
      },
    ],
  };

  if (slashBlockShouldBeReplaced(block.content)) {
    editor.updateBlock(block, linkParagraph);
    editor.setTextCursorPosition(block, 'end');
    return;
  }

  const [inserted] = editor.insertBlocks([linkParagraph], block, 'after');
  if (inserted) {
    editor.setTextCursorPosition(inserted, 'end');
  }
}

export function insertDatabaseEmbedAtCursor(
  editor: BlockNoteEditor,
  databaseId: string,
  options?: {
    viewType?: DatabaseEmbedViewType;
    viewConfigJson?: string;
  },
): void {
  const viewType = options?.viewType ?? DEFAULT_DATABASE_EMBED_VIEW_TYPE;
  const viewConfigJson = options?.viewConfigJson ?? '';
  const slashBlock = editor.getTextCursorPosition().block;
  const embedId = newBlockId();
  const embed = {
    id: embedId,
    type: DATABASE_EMBED_BLOCK_TYPE,
    props: { databaseId, viewType, viewConfigJson },
  } as unknown as Parameters<typeof editor.insertBlocks>[0][number];

  const fresh = editor.getBlock(slashBlock.id) ?? slashBlock;
  if (slashBlockShouldBeReplaced(fresh.content)) {
    editor.replaceBlocks([fresh.id], [embed]);
  } else {
    editor.insertBlocks([embed], fresh.id, 'after');
  }

  if (editor.getBlock(embedId)) {
    const [trailing] = editor.insertBlocks([{ type: 'paragraph' }], embedId, 'after');
    if (trailing) {
      editor.setTextCursorPosition(trailing, 'end');
    }
  }
}

async function createDatabaseBlock(
  pageId: string,
  id: string,
  title: string,
): Promise<BlockRecord> {
  const siblings = await fetchBlockTree(pageId);
  const position = siblings.filter((b) => b.type === 'page' || b.type === 'database').length;
  return createBlock({
    id,
    parentId: pageId,
    type: 'database',
    title,
    position,
  });
}

export async function insertDatabaseFromSlash(
  editor: BlockNoteEditor,
  pageId: string,
  options?: {
    title?: string;
    mode?: 'inline' | 'link';
    navigateToDatabase?: (databaseId: string) => void;
    onCreated?: (block: BlockRecord, parentId: string) => void;
  },
): Promise<BlockRecord> {
  const title = options?.title?.trim() || DEFAULT_TITLE;
  const id = newBlockId();
  const mode = options?.mode ?? 'link';

  if (mode === 'inline') {
    // Capture the slash block before the async create so we can replace it once
    // the database exists on the server (avoids a 404 flash in the embed).
    const slashBlock = editor.getTextCursorPosition().block;

    const block = await createDatabaseBlock(pageId, id, title);
    options?.onCreated?.(block, pageId);

    editor.setTextCursorPosition(slashBlock, 'end');
    insertDatabaseEmbedAtCursor(editor, id);

    if (options?.navigateToDatabase) {
      options.navigateToDatabase(id);
    }

    return block;
  }

  // Link mode: insert a reference paragraph synchronously while the slash-menu
  // cursor context is fresh, then create the database with the same id.
  const href = `/database/${id}`;
  insertDatabaseLinkParagraph(editor, href, title);

  const block = await createDatabaseBlock(pageId, id, title);
  options?.onCreated?.(block, pageId);

  if (options?.navigateToDatabase) {
    options.navigateToDatabase(id);
  }

  return block;
}
